import {useShows} from "store/show/show.store";
import {useEffect, useState} from "react";
import uniqid from "uniqid";
import {
  INotificationBaseRelatedToShow,
  IPayloadUpdatedShow,
  IShow,
  NotificationType,
  Roles,
} from "@evennt-tech/api-v2-core";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import {apiV2} from "api/firebase";

type UseOrganiserShowsResult = [IShow[], boolean];

const useOrganiserShows = (): UseOrganiserShowsResult => {
  const {
    fetchRelatedShowByOrganiserUser,
    itemsByOrganiserUser,
    isLoading: isOrganiserShowLoading,
  } = useShows();
  const {role} = useSelector(selectUser);
  // Layout, the Stripe Connect bar and Settings mount this hook at the same
  // time. A listener id shared between them let the first one to unmount
  // unsubscribe all the others, and the list stayed stale after that.
  const [listenerId] = useState(() => uniqid("use_organiser_shows_"));

  useEffect(() => {
    if (role !== Roles.ORGANISER) return;
    fetchRelatedShowByOrganiserUser({});
  }, [role]);

  // CREATED_SHOW is published on the common (show-less) channel.
  useEffect(() => {
    if (role !== Roles.ORGANISER) return;

    apiV2.notification.subscribe({
      key: NotificationType.CREATED_SHOW,
      listenerId,
      callback: () => fetchRelatedShowByOrganiserUser({}, true),
    });

    return () => {
      apiV2.notification.unsubscribe({
        key: NotificationType.CREATED_SHOW,
        listenerId,
      });
    };
  }, [role, listenerId, fetchRelatedShowByOrganiserUser]);

  useEffect(() => {
    if (!itemsByOrganiserUser.length) return;

    for (const show of itemsByOrganiserUser) {
      apiV2.notification.subscribe<
        INotificationBaseRelatedToShow<IPayloadUpdatedShow>
      >({
        key: NotificationType.UPDATED_SHOW,
        showId: show.id,
        listenerId,
        callback: async ({payload}) => {
          if (payload?.showId !== show.id) return;
          await fetchRelatedShowByOrganiserUser({}, true);
        },
      });
    }

    return () => {
      for (const show of itemsByOrganiserUser) {
        apiV2.notification.unsubscribe({
          key: NotificationType.UPDATED_SHOW,
          listenerId,
          showId: show.id,
        });
      }
    };
  }, [itemsByOrganiserUser, listenerId, fetchRelatedShowByOrganiserUser]);

  const isLoading = isOrganiserShowLoading;
  return [itemsByOrganiserUser, isLoading];
};

export default useOrganiserShows;
